import { useCallback, useEffect, useMemo, useState } from 'react';
import { ImageResolution } from '../types';

// Thresholds to try for each image resolution -
// larger resolutions start with more memory
const MEMORY_THRESHOLDS: Record<ImageResolution, number[]> = {
  '480': [128, 256, 512],
  '720': [128, 256, 512],
  '1080': [128, 256, 512],
  '1440': [128, 256, 512],
  '2160': [256, 512, 1024], // 4k
  '4320': [512, 1024], // 8k
};

type UseWorkerMemoryManagementReturn = {
  currentMemoryThreshold: number;
  maxMemoryReached: boolean;
  increaseMemory: () => void;
};

export const useWorkerMemoryManagement = (
  imageResolution: ImageResolution,
  maxMemory = 1024
): UseWorkerMemoryManagementReturn => {
  const [currentMemoryIndex, setCurrentMemoryIndex] = useState(0);
  const [memoryThresholdsExhausted, setMemoryThresholdsExhausted] =
    useState(false);

  const increaseMemory = useCallback(() => {
    setCurrentMemoryIndex((prev) => {
      if (prev < MEMORY_THRESHOLDS[imageResolution].length - 1) {
        return prev + 1;
      }
      setMemoryThresholdsExhausted(true);
      return prev;
    });
  }, [imageResolution]);

  const resetMemoryManagement = useCallback(() => {
    setCurrentMemoryIndex(0);
    setMemoryThresholdsExhausted(false);
  }, []);

  useEffect(() => {
    resetMemoryManagement();
  }, [imageResolution, resetMemoryManagement]);

  const currentMemoryThreshold = useMemo(() => {
    return Math.min(
      maxMemory,
      MEMORY_THRESHOLDS[imageResolution][currentMemoryIndex]
    );
  }, [currentMemoryIndex, imageResolution, maxMemory]);

  return {
    currentMemoryThreshold,
    maxMemoryReached:
      memoryThresholdsExhausted || currentMemoryThreshold >= maxMemory,
    increaseMemory,
  };
};
